import { NextRouter } from "next/router"
import { isTripRef, TripRef } from "@/types/trip"

import { routes } from "@/lib/routes"

const chatRoutes = [routes.chat]

/** Numeric conversation id from a chat route match, else null. */
function chatIdOf(match: { id?: readonly string[] } | null): number | null {
  const id = match?.id?.[0]
  return id && /^\d+$/.test(id) ? Number.parseInt(id, 10) : null
}

export function isChatPage(router: NextRouter) {
  const currentURL = router.asPath
  // Regular expression that match /chat/123 or /chat/Ka'ana-Kitchen/Restaurant-2461 (chat with place sheet) or /new
  // We are not that strict about the params because that set is expandable (e.g., utm).
  const chatPathRegex =
    /^\/chat((\/\d+)|(\/.+\/.+)|(.*flight-search.*)|(\/new))?(\?.*)?$/
  return chatPathRegex.test(currentURL)
}

/** `/chat/{numericId}` conversation view. */
export function isChatConversationRoute(router: NextRouter) {
  return chatIdOf(routes.chat.match(router.asPath)) !== null
}

/**
 * Matches a chat conversation pathname (`/chat/{id}`). Used for nav
 * highlighting where the pathname (not the NextRouter) is the source of truth.
 */
export function isChatConversationPath(path: string) {
  return getChatIdFromConversationPath(path) !== null
}

/** Numeric chat id from `/chat/{id}`, if present. */
export function getChatIdFromConversationPath(path: string): number | null {
  for (const route of chatRoutes) {
    const id = chatIdOf(route.match(path))
    if (id !== null) return id
  }
  return null
}

/** User-created trip chats belong under Trips in site nav, not Chats. */
export function shouldHighlightTripsNavForUserCreatedChat(
  path: string,
  tripIsUserCreated: boolean | undefined,
  embeddedViewType?: string
) {
  if (!tripIsUserCreated) return false
  if (embeddedViewType === "chat") return true
  return isChatConversationPath(path)
}

/** Bare `/home` with optional query string (main-app home, not embedded). */
export function isAppHomePath(path: string) {
  const barePath = path.split("?")[0].split("#")[0]
  return barePath === "/home"
}

/** App home is `/home` in the main app. Prefer `useIsAppHomeRoute` in components. */
export function isAppHomeRoute(router: NextRouter, embedded: boolean) {
  if (embedded) return false
  return isAppHomePath(router.asPath)
}

export function isChatPageUrl(url: string) {
  return chatRoutes.some((route) => route.test(url))
}

export function isExplorePageUrl(url: string) {
  return routes.explore.test(url)
}

export function isTripPageUrl(url: string) {
  return routes.trip.test(url)
}

/** The trip ref from a `/trip/{ref}` URL */
export function tripRefFromUrl(url: string): TripRef | undefined {
  const ref = routes.trip.match(url)?.slug?.[0]
  return ref && isTripRef(ref) ? ref : undefined
}
